import { useEffect, useMemo, useState } from 'react';
import { useMarketplaceProducts } from '@/hooks/supabase/useMarketplaceProducts';
import { useMapleMarketLicenses } from '@/hooks/supabase/useMapleMarketLicenses';
import { useMarketplaceWishlist } from '@/hooks/supabase/useMarketplaceWishlist';
import {
  readInstalledMapleMarketProducts,
  type MapleMarketInstalledRecord,
} from '@/lib/mapleMarketLibrary';

export type MapleMarketLibraryEntry = {
  product: ReturnType<typeof useMarketplaceProducts>['products'][number];
  owned: boolean;
  installed: boolean;
  favorite: boolean;
  installRecord: MapleMarketInstalledRecord | null;
};

export function useMapleMarketLibrary() {
  const { products, loading, error, reload } = useMarketplaceProducts();
  const ownedSkus = useMapleMarketLicenses(products.map((product) => product.sku));
  const { wishlistedSkus, toggleWishlist } = useMarketplaceWishlist(products.map((product) => product.sku));
  const [installedMap, setInstalledMap] = useState<Record<string, MapleMarketInstalledRecord>>({});

  useEffect(() => {
    const refreshInstalled = () => {
      setInstalledMap(readInstalledMapleMarketProducts());
    };

    refreshInstalled();
    window.addEventListener('maple-market:library-refresh', refreshInstalled);
    return () => window.removeEventListener('maple-market:library-refresh', refreshInstalled);
  }, []);

  const entries = useMemo<MapleMarketLibraryEntry[]>(() => {
    return products
      .filter((product) => ownedSkus.has(product.sku) || Boolean(installedMap[product.sku]))
      .map((product) => ({
        product,
        owned: ownedSkus.has(product.sku),
        installed: Boolean(installedMap[product.sku]),
        favorite: wishlistedSkus.has(product.sku),
        installRecord: installedMap[product.sku] ?? null,
      }))
      .sort((left, right) => {
        const leftTime = left.installRecord?.lastPlayedAt || left.installRecord?.installedAt || left.product.releaseDate;
        const rightTime = right.installRecord?.lastPlayedAt || right.installRecord?.installedAt || right.product.releaseDate;
        return new Date(rightTime).getTime() - new Date(leftTime).getTime();
      });
  }, [installedMap, ownedSkus, products, wishlistedSkus]);

  const stats = useMemo(() => ({
    total: entries.length,
    installed: entries.filter((entry) => entry.installed).length,
    readyToInstall: entries.filter((entry) => !entry.installed && entry.owned).length,
    favorites: entries.filter((entry) => entry.favorite).length,
  }), [entries]);

  return {
    entries,
    stats,
    loading,
    error,
    reload,
    toggleWishlist,
  };
}
